import React, { createContext, useContext, useRef, useCallback, type ReactNode } from 'react';
import { useSocketConnectionId } from './SocketContext';
import InfoWindow from '../components/InfoWindow/InfoWindow';
import type { OpenInfoWindowFn, OpenInfoWindowOptions } from '../components/InfoWindow/types';

interface InfoWindowContextType {
  openInfoWindow: OpenInfoWindowFn;
  closeInfoWindow: () => void;
  registerInfoWindow: (fn: OpenInfoWindowFn) => void;
  registerUpdateCustomFields: (
    fn: (fields: any, conversation: any, updatedField: any) => void
  ) => void;
}

// Exported for use in other packages (e.g., chat)
export const InfoWindowContext = createContext<InfoWindowContextType | undefined>(undefined);

export const useInfoWindow = () =>
{
  const context = useContext(InfoWindowContext);
  if (!context)
  {
    throw new Error('useInfoWindow must be used within an InfoWindowProvider');
  }
  return context;
};

interface InfoWindowProviderProps {
  children: ReactNode;
  api: any;
  config: any;
  storageData: any;
  userData: any;
  realItemTypes: any;
  functionsLoad: any;
}

export const InfoWindowProvider: React.FC<InfoWindowProviderProps> = ({
  children,
  api,
  config,
  storageData,
  userData,
  realItemTypes,
  functionsLoad,
}) =>
{
  const socketConnectionId = useSocketConnectionId();
  const openInfoWindowFnRef = useRef<OpenInfoWindowFn | null>(null);
  const updateCustomFieldsFnRef = useRef<
    ((fields: any, conversation: any, updatedField: any) => void) | null
      >(null);

  const registerInfoWindow = useCallback(
    (fn: OpenInfoWindowFn) =>
    {
      openInfoWindowFnRef.current = fn;
    },
    [],
  );

  const registerUpdateCustomFields = useCallback(
    (fn: (fields: any, conversation: any, updatedField: any) => void) =>
    {
      updateCustomFieldsFnRef.current = fn;
    },
    [],
  );

  const openInfoWindow = useCallback((
    conversationId: number,
    recycleBin?: boolean,
    conversation?: any,
    options?: OpenInfoWindowOptions,
  ) =>
  {
    if (openInfoWindowFnRef.current)
    {
      openInfoWindowFnRef.current(conversationId, recycleBin, conversation, options);
    }
    else
    {
      console.warn('InfoWindow not registered or not ready.');
    }
  }, []);

  const closeInfoWindow = useCallback(() => {}, []);

  const handleUpdateCustomFields = useCallback(
    (fields: any, conversation: any, updatedField: any) =>
    {
      updateCustomFieldsFnRef.current?.(fields, conversation, updatedField);
    },
    [],
  );

  return (
    <InfoWindowContext.Provider
      value={{ openInfoWindow, closeInfoWindow, registerInfoWindow, registerUpdateCustomFields }}
    >
      {children}
      <InfoWindow
        api={api}
        config={config}
        storageData={storageData}
        userData={userData}
        realItemTypes={realItemTypes}
        socket={socketConnectionId}
        getOpenInfoWindowFn={registerInfoWindow}
        functionsLoad={functionsLoad}
        functions={{ UpdateCustomFields: handleUpdateCustomFields }}
      />
    </InfoWindowContext.Provider>
  );
};
